import { useAtomValue } from 'jotai'
import { useAtomCallback } from 'jotai/utils'
import { useCallback, useMemo, type FC } from 'react'

import { Modal } from '@/components/elements'
import { MENU } from '@/constants/common/message.constants'
import {
  type ImportHandoff,
  ShipmentOrderCsvImportModalContent
} from '@/features/shipment-order-csv-import'
import { useCustomShipmentOrderImports } from '@/features/shipment-order-import-jobs/hooks'

import { isOpenShipmentOrderCsvImportModalAtom } from '../../-states'

type Props = {
  tenantKey: string
}

/** 拠点で出し分ける取り込み（useCustomShipmentOrderImports の key）→ サントリー様の読み方 */
const SUNTORY_KIND_OF_KEY: Partial<Record<string, ImportHandoff['kind']>> = {
  'suntory-internal': 'internal',
  'suntory-dedicated': 'dedicated'
}

/**
 * 配送依頼のインポートの入口 Modal（#4728）。最初の画面で取り込み方法を選ぶ。
 * 通常フォーマット・保存したフォーマット・フォーマット外に加え、サントリー様の社内移動と
 * 得意先（どちらも拠点で出し分け）もこの Modal で確認画面を通して取り込む（取込の経路・パーサーは既存のまま）。
 */
export const ShipmentOrderCsvImportModal: FC<Props> = ({ tenantKey }) => {
  const isOpen = useAtomValue(isOpenShipmentOrderCsvImportModalAtom)

  const handleClose = useAtomCallback(
    useCallback((_, set) => {
      set(isOpenShipmentOrderCsvImportModalAtom, false)
    }, [])
  )

  // サントリー様の取り込み（社内移動・得意先）は拠点で出し分ける（useCustomShipmentOrderImports の判定をそのまま使う）
  const customImports = useCustomShipmentOrderImports(tenantKey)
  const handoffs = useMemo<ImportHandoff[]>(
    () =>
      customImports.flatMap((item) => {
        const kind = SUNTORY_KIND_OF_KEY[item.key]
        return kind
          ? [
              {
                key: item.key,
                title: item.label,
                description: item.description,
                kind
              }
            ]
          : []
      }),
    [customImports]
  )

  return (
    <Modal
      title={MENU.IMPORTING_SHIPMENT_ORDER_CSV}
      // 確認画面（エラーの修正）の表に画面を回すため、ほぼ全画面で開く。ステップごとに
      // 大きさを変えると、戻る・進むのたびに枠とボタンの位置が跳ねるので全ステップ共通にする。
      width="96vw"
      style={{ top: 16 }}
      open={isOpen}
      onCancel={handleClose}
    >
      <ShipmentOrderCsvImportModalContent
        key={tenantKey}
        tenantKey={tenantKey}
        onClose={handleClose}
        handoffs={handoffs}
      />
    </Modal>
  )
}
